import { useState, useEffect, useCallback } from 'react';
import { shareLinkApi, ShareLinkDto, CreateShareLinkRequest, UpdateShareLinkRequest } from '../services/shareLinkApi';

export const useShareLinks = () => {
  const [shareLinks, setShareLinks] = useState<ShareLinkDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchShareLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await shareLinkApi.getShareLinks();
      setShareLinks(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch share links';
      setError(errorMessage);
      console.error('Error fetching share links:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createShareLink = useCallback(async (data: CreateShareLinkRequest): Promise<boolean> => {
    try {
      setError(null);
      await shareLinkApi.createShareLink(data);
      // Don't update state here - let fetchShareLinks() reload all data to get latest values
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create share link';
      setError(errorMessage);
      console.error('Error creating share link:', err);
      return false;
    }
  }, []);

  const updateShareLink = useCallback(async (id: number, data: UpdateShareLinkRequest): Promise<boolean> => {
    try {
      setError(null);
      const updatedShareLink = await shareLinkApi.updateShareLink(id, data);
      setShareLinks(prev => prev.map(link => link.id === id ? updatedShareLink : link));
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update share link';
      setError(errorMessage);
      console.error('Error updating share link:', err);
      return false;
    }
  }, []);

  const deleteShareLink = useCallback(async (id: number): Promise<boolean> => {
    try {
      setError(null);
      const success = await shareLinkApi.deleteShareLink(id);
      if (success) {
        setShareLinks(prev => prev.filter(link => link.id !== id));
      }
      return success;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete share link';
      setError(errorMessage);
      console.error('Error deleting share link:', err);
      return false;
    }
  }, []);

  const getShareLinkById = useCallback(async (id: number): Promise<ShareLinkDto | null> => {
    try {
      setError(null);
      const shareLink = await shareLinkApi.getShareLinkById(id);
      return shareLink;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch share link';
      setError(errorMessage);
      console.error('Error fetching share link:', err);
      return null;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch share links on mount
  useEffect(() => {
    fetchShareLinks();
  }, [fetchShareLinks]);

  return {
    shareLinks,
    loading,
    error,
    createShareLink,
    updateShareLink,
    deleteShareLink,
    getShareLinkById,
    fetchShareLinks,
    clearError,
  };
};
